import { Alert, Box, Button, Divider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { HUB_SIGNIN_ERROR_MESSAGES, startHubSignIn } from 'services/hub-auth-service';
import type { HubSignInErrorCode } from 'services/hub-auth-service.types';

type HubSignInProps = {
  /** Why the last sign-in through the Hub failed, shown until the next attempt. */
  error: HubSignInErrorCode | null;
};

const HubSignIn = ({ error }: HubSignInProps) => {
  const [shownError, setShownError] = useState(error);
  const [redirecting, setRedirecting] = useState(false);
  // A state update is not seen by a second click in the same frame.
  const redirectingRef = useRef(false);

  // Coming back from the Hub restores this page from the bfcache as it was left.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        redirectingRef.current = false;
        setRedirecting(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const signIn = async () => {
    if (redirectingRef.current) {
      return;
    }
    redirectingRef.current = true;
    setRedirecting(true);
    setShownError(null);
    try {
      await startHubSignIn();
    } catch (startError) {
      redirectingRef.current = false;
      setRedirecting(false);
      setShownError('start_failed');
    }
  };

  // No focus change on mousedown: a focused field blurring re-validates the
  // form below, the layout shifts and the click misses the button.
  return (
    <Box sx={{ maxWidth: 390, mx: 'auto', mb: 3 }}>
      {shownError && (
        <Alert severity='error' sx={{ mb: 2, textAlign: 'left' }}>
          {HUB_SIGNIN_ERROR_MESSAGES[shownError]}
        </Alert>
      )}
      <Button
        type='button'
        variant='outlined'
        size='large'
        fullWidth
        disabled={redirecting}
        aria-busy={redirecting}
        onMouseDown={(event) => event.preventDefault()}
        onClick={signIn}>
        Sign in with Enara Hub
      </Button>
      <Divider sx={{ mt: 3 }}>or</Divider>
    </Box>
  );
};

export default HubSignIn;
